import styled from "styled-components";

/*
 * Every consumer puts this inside a `flex-direction: column` parent, so it
 * takes its height from the flex line instead of `height: 100%`: in the cards
 * with a definite height (BimClass, Parameter, …) that fills exactly the same
 * space, and in a stack that scrolls as a whole (Category detail) it sizes to
 * content instead of resolving the percentage against an auto-height parent
 * and collapsing the JSON editor to a couple of pixels.
 */
export const StyledEditorLayout = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 16px;
    min-height: 0;

    .mode-tabs {
        align-self: flex-start;
        background: #f4f5f7;
        border-radius: 6px;
        display: flex;
        gap: 4px;
        padding: 3px;

        button {
            align-items: center;
            background: transparent;
            border: none;
            border-radius: 4px;
            color: #6b778c;
            cursor: pointer;
            display: flex;
            font-size: 0.85rem;
            font-weight: 500;
            gap: 6px;
            padding: 6px 12px;

            &:hover {
                color: #172b4d;
            }

            &.active {
                background: #fff;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
                color: #0052cc;
            }
        }
    }

    /*
     * flex-basis stays auto: inside a card with a definite height this still
     * grows to fill and scrolls (min-height:0 lets it shrink past its content),
     * but on a page that scrolls as a whole — Categories — the container height
     * is indefinite, and a 0 basis there collapses the body to nothing.
     */
    .editor-body {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        gap: 16px;
        min-height: 0;
        overflow-y: auto;
        padding-bottom: 24px;
    }

    /*
     * The field holding the JSON editor takes all leftover height in
     * .editor-body; min-height:0 lets it shrink past Monaco's own content so
     * the wrapper — not the field — decides the floor.
     */
    .json-field {
        flex: 1 1 auto;
        min-height: 0;
    }

    /*
     * The editor is the whole point of the JSON tab, so it takes every pixel the
     * card has left. The viewport-relative floor is what makes it usable on the
     * screens whose column scrolls as a whole (Categories) — there the flex line
     * has no definite height to grow into, so flex-grow alone left a stub.
     */
    .json-editor-wrapper {
        border: 1px solid #e1e4e8;
        border-radius: 6px;
        display: flex;
        flex: 1 1 auto;
        min-height: min(70vh, 900px);
        overflow: hidden;
        position: relative;
        width: 100%;
    }

    .groups-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        padding-bottom: 16px;

        .group-card {
            background: #fafbfc;
            border: 1px solid #ebecf0;
            border-radius: 6px;
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding: 12px;

            .group-header {
                align-items: center;
                display: flex;
                justify-content: space-between;
            }
        }
    }

    .images-list {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .image-row {
            align-items: center;
            display: flex;
            gap: 8px;
        }

        /* 64px, matching the single-image .preview below: these are product
         * renders, and at 32px they were too small to tell apart. */
        .img-preview {
            background: #0001;
            border-radius: 4px;
            flex: none;
            height: 64px;
            object-fit: contain;
            padding: 4px;
            width: 64px;
        }
    }

    .preview {
        background: #0001;
        border-radius: 4px;
        margin-top: 8px;
        max-height: 64px;
        max-width: 64px;
        padding: 4px;
    }

    .errors {
        color: #c00;
        font-size: 0.9rem;
        list-style: none;
        margin: 0;
        padding: 0;
    }
`;
